/**
 * Wall placement / blocking rules.
 *
 * Coordinate convention (docs/CORE_INTERFACE.md):
 * - A wall spans 2 cells. Its anchor is the intersection (col, row) with
 *   0 <= col, row <= size - 2. Intersection (c, r) is the bottom-right
 *   corner of cell (c, r).
 * - 'h' wall at (c, r): blocks vertical movement (c,r)<->(c,r+1) and
 *   (c+1,r)<->(c+1,r+1).
 * - 'v' wall at (c, r): blocks horizontal movement (c,r)<->(c+1,r) and
 *   (c,r+1)<->(c+1,r+1).
 *
 * Pure functions only. No dependency on the full game state shape beyond
 * the walls array / boardSize, so path search can reuse them cheaply.
 */

/** Stable string key for a wall position+orientation. */
export function wallKey(col, row, orientation) {
  return orientation + ':' + col + ',' + row;
}

/** Build a Set of wall keys for O(1) lookup during search. */
export function buildWallSet(walls) {
  const set = new Set();
  for (const w of walls) {
    set.add(wallKey(w.col, w.row, w.orientation));
  }
  return set;
}

/** Is the wall anchor within the legal anchor range? */
export function wallInBounds(boardSize, col, row) {
  return (
    Number.isInteger(col) &&
    Number.isInteger(row) &&
    col >= 0 &&
    row >= 0 &&
    col <= boardSize - 2 &&
    row <= boardSize - 2
  );
}

/**
 * Does a candidate wall overlap/cross an existing wall?
 * Contract rules:
 * - h at (c,r) conflicts with h at (c-1,r) / (c,r) / (c+1,r) and v at (c,r)
 * - v at (c,r) conflicts with v at (c,r-1) / (c,r) / (c,r+1) and h at (c,r)
 */
export function wallConflicts(wallSet, col, row, orientation) {
  if (orientation === 'h') {
    return (
      wallSet.has(wallKey(col - 1, row, 'h')) ||
      wallSet.has(wallKey(col, row, 'h')) ||
      wallSet.has(wallKey(col + 1, row, 'h')) ||
      wallSet.has(wallKey(col, row, 'v'))
    );
  }
  return (
    wallSet.has(wallKey(col, row - 1, 'v')) ||
    wallSet.has(wallKey(col, row, 'v')) ||
    wallSet.has(wallKey(col, row + 1, 'v')) ||
    wallSet.has(wallKey(col, row, 'h'))
  );
}

/**
 * Is the single step between two ADJACENT cells blocked by a wall?
 * `from` and `to` must be orthogonally adjacent.
 *
 * This is the single chokepoint for "is this edge walled" decisions.
 * Phase 2 note: the ninja's wall jump is modeled as an explicit ability
 * move (abilities.js) and as a jump budget in the path BFS (path.js) —
 * this geometric predicate itself is never relaxed.
 */
export function stepBlockedByWall(wallSet, from, to) {
  if (from.col === to.col) {
    // vertical step: blocked by h wall at (col-1, minRow) or (col, minRow)
    const r = Math.min(from.row, to.row);
    return (
      wallSet.has(wallKey(from.col - 1, r, 'h')) ||
      wallSet.has(wallKey(from.col, r, 'h'))
    );
  }
  // horizontal step: blocked by v wall at (minCol, row-1) or (minCol, row)
  const c = Math.min(from.col, to.col);
  return (
    wallSet.has(wallKey(c, from.row - 1, 'v')) ||
    wallSet.has(wallKey(c, from.row, 'v'))
  );
}
